/**
 * La vue « Collecte du mois » de la liste des dossiers.
 *
 * CE N'EST PAS UN ÉCRAN DE PLUS. Décision de Rose du 23/08/2026 : plutôt
 * qu'une nouvelle entrée de menu pour suivre la collecte, on change de lunettes
 * sur la liste qu'on regarde déjà. Le menu est le seul endroit du logiciel
 * qu'on ne peut pas replier ; une entrée par tâche et il devient à lui seul la
 * difficulté principale.
 *
 * CE QU'ELLE MONTRE, ET RIEN D'AUTRE. Par défaut, les seuls dossiers qui
 * demandent un geste : ceux qui n'ont rien envoyé. Ceux qui ont répondu sont
 * comptés en tête mais pas listés — les afficher obligerait à trier soi-même,
 * ce qui est exactement le travail qu'on retire.
 *
 * ⚠️ L'ÉCRAN NE DÉCIDE JAMAIS QUI RELANCER. Il demande, le serveur choisit. Un
 * affichage en retard d'un rafraîchissement ferait sinon écrire à quelqu'un qui
 * vient de répondre.
 */
import { useEffect, useState } from 'react';
import {
  Send,
  Mail,
  PencilLine,
  RotateCcw,
  Check,
  AlertCircle,
  Circle,
  Eye,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import {
  collecteService,
  type BilanRelance,
  type LigneCollecte,
} from '../services/collecte.service';

const MOIS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

/**
 * Une case du tableau.
 *
 * LA COULEUR EST RÉSERVÉE À CE QUI CLOCHE. Une date informe — « le 05/08 » dit
 * quand ; une pastille verte oblige à se souvenir de ce que vert veut dire, et
 * dix pastilles vertes ne disent rien du tout. Ce qui est normal reste gris ;
 * l'œil va alors droit aux deux ou trois lignes qui demandent un geste.
 */
function Case({ etat, texte }: { etat: string; texte: string }) {
  /**
   * CHAQUE ÉTAT PORTE UNE ICÔNE, sans exception.
   *
   * Une icône se comporte comme un caractère : elle occupe de la place dans la
   * ligne et pousse le texte qui suit. Quand seules certaines lignes en ont, les
   * textes ne commencent plus au même endroit et la colonne se lit en dents de
   * scie — Rose l'a repéré le 23/08/2026, et centrer n'y changeait rien
   * puisque centrer aligne les milieux, pas les débuts.
   *
   * Le cercle vide de l'attente est volontairement discret : il tient la
   * colonne sans rien signaler. Ce qui doit accrocher l'œil, c'est le rouge.
   */
  if (etat === 'fait') {
    return (
      <span className="flex items-center gap-1.5 text-[12.5px] text-neutral-600">
        <Check className="h-3.5 w-3.5 shrink-0 text-[#1D9E75]" />
        {texte}
      </span>
    );
  }
  if (etat === 'echec' || etat === 'en_retard') {
    return (
      <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-red-700">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {texte}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-[12.5px] text-neutral-400">
      <Circle className="h-3.5 w-3.5 shrink-0 text-neutral-300" />
      {texte}
    </span>
  );
}

/** L'état de la collecte, dans le même vocabulaire que les autres colonnes. */
function CaseVariables({ ligne }: { ligne: LigneCollecte }) {
  /**
   * CE QUI DORT DEPUIS TROP LONGTEMPS PASSE DEVANT LE RESTE.
   *
   * Demande de Rose du 23/08/2026 : « attention, le client a envoyé des
   * variables il y a deux jours ». Reçu et bulletins pas faits, le tableau le
   * montrait déjà ; ce qu'il ne montrait pas, c'est le TEMPS. Une case verte
   * « le 08/09 » se lit comme une bonne nouvelle alors qu'elle signale un
   * dossier oublié.
   *
   * L'ambre, pas le rouge : personne n'est en faute, la paie n'est pas en
   * retard — c'est un rappel, et le rouge reste réservé aux vrais retards.
   */
  if (ligne.enSouffranceDepuis !== null) {
    // La case ne dit plus la date exacte ni le canal : l'infobulle les garde,
    // pour qu'alerter ne fasse rien PERDRE de ce qui s'affichait avant.
    const arrivee = ligne.soumisLe ?? ligne.recuHorsLien;
    const quand = arrivee
      ? new Date(arrivee).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
        })
      : null;
    const parQuelMoyen = ligne.soumisLe
      ? 'par le lien'
      : ligne.recuHorsLienMotif
        ? `hors lien — ${ligne.recuHorsLienMotif}`
        : 'hors lien';
    return (
      <span
        className="flex items-center gap-1.5 text-[12.5px] font-medium text-amber-700"
        title={`Variables reçues${quand ? ` le ${quand}` : ''} ${parQuelMoyen}. Les bulletins ne sont pas faits.`}
      >
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        reçues il y a {ligne.enSouffranceDepuis} j
      </span>
    );
  }
  if (ligne.etat === 'recu') {
    return (
      <Case
        etat="fait"
        texte={
          ligne.soumisLe
            ? `le ${new Date(ligne.soumisLe).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}`
            : 'reçues'
        }
      />
    );
  }
  if (ligne.etat === 'recu_autrement') {
    return (
      <span title={ligne.recuHorsLienMotif ?? undefined}>
        <Case etat="fait" texte="hors lien" />
      </span>
    );
  }
  if (ligne.etat === 'sans_lien') {
    // Ce dossier est passé à travers la campagne. Ce n'est pas la même chose
    // qu'un client qui n'a pas répondu, et ça ne se répare pas en relançant.
    return (
      <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-amber-700">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        aucun lien
      </span>
    );
  }
  // « 1 relance » sur dix lignes n'apprend rien : c'est le cas ordinaire. Le
  // compte ne s'affiche qu'à partir de la DEUXIÈME, là où il signale un client
  // qui ne répond pas.
  return (
    <Case
      etat="attendu"
      texte={ligne.relances > 1 ? `${ligne.relances} relances` : 'en attente'}
    />
  );
}

export function VueCollecte({
  recherche,
  peutAgir = true,
}: {
  recherche?: string;
  /** Un lecteur voit la collecte ; il ne relance pas, ne saisit pas, ne marque rien. */
  peutAgir?: boolean;
}) {
  const aujourdhui = new Date();
  const [mois, setMois] = useState(aujourdhui.getMonth() + 1);
  const [annee, setAnnee] = useState(aujourdhui.getFullYear());
  const [lignes, setLignes] = useState<LigneCollecte[]>([]);
  const [charge, setCharge] = useState(false);
  const [encours, setEncours] = useState(false);
  const [bilan, setBilan] = useState<BilanRelance | null>(null);
  /**
   * Par défaut, TOUT le portefeuille est affiché : c'est un tableau de
   * production, pas une liste de relances. Un dossier dont les variables sont
   * arrivées mais dont les bulletins ne sont pas faits demande autant
   * d'attention qu'un client qui n'a rien envoyé.
   */
  const [seulementAFaire, setSeulementAFaire] = useState(false);
  const [designation, setDesignation] = useState<LigneCollecte | null>(null);
  const [motif, setMotif] = useState('');

  const recharger = async () => {
    setCharge(false);
    try {
      setLignes(await collecteService.duMois(mois, annee));
    } finally {
      setCharge(true);
    }
  };

  useEffect(() => {
    void recharger();
  }, [mois, annee]);

  const repondus = lignes.filter(
    l => l.etat === 'recu' || l.etat === 'recu_autrement'
  ).length;
  const relancables = lignes.filter(l => l.relancable).length;
  const bulletinsFaits = lignes.filter(l => l.bulletins.etat === 'fait').length;
  const dsnFaites = lignes.filter(l => l.dsn.etat === 'fait').length;

  const filtre = recherche?.trim().toLowerCase();
  /** Un dossier « fini » : les trois étapes sont faites, il n'appelle rien. */
  const termine = (l: LigneCollecte) =>
    (l.etat === 'recu' || l.etat === 'recu_autrement') &&
    l.bulletins.etat === 'fait' &&
    l.dsn.etat === 'fait';

  const visibles = lignes
    .filter(l => (seulementAFaire ? !termine(l) : true))
    .filter(l => (filtre ? l.dossier.toLowerCase().includes(filtre) : true));
  const finis = lignes.filter(termine).length;
  /**
   * Les dossiers qui dorment. Ce compte ne s'affiche QUE s'il y en a : une
   * ligne « 0 en attente » chaque jour finirait par ne plus être lue, et le
   * jour où elle dirait 4, personne ne la verrait.
   */
  const enSouffrance = lignes.filter(l => l.enSouffranceDepuis !== null).length;

  const relancer = async () => {
    setEncours(true);
    try {
      setBilan(await collecteService.relancer(mois, annee));
      await recharger();
    } finally {
      setEncours(false);
    }
  };

  const designer = async () => {
    if (!designation || !motif.trim()) return;
    setEncours(true);
    try {
      await collecteService.recuAutrement(
        designation.folderUuid,
        mois,
        annee,
        motif.trim()
      );
      setDesignation(null);
      setMotif('');
      await recharger();
    } finally {
      setEncours(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Une phrase, pas des cartes d'indicateurs : le compte se lit d'un trait. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13.5px] text-neutral-700">
          <select
            value={`${annee}-${mois}`}
            onChange={e => {
              const [a, m] = e.target.value.split('-');
              setAnnee(Number(a));
              setMois(Number(m));
            }}
            aria-label="Mois de la collecte"
            className="mr-1 rounded-lg border border-[var(--color-primary-100)] bg-white px-2 py-1 text-[13px] outline-none focus:ring-2 focus:ring-[var(--color-primary-400)]"
          >
            {[0, 1, 2].map(recul => {
              const d = new Date(
                aujourdhui.getFullYear(),
                aujourdhui.getMonth() - recul,
                1
              );
              return (
                <option
                  key={recul}
                  value={`${d.getFullYear()}-${d.getMonth() + 1}`}
                >
                  {MOIS[d.getMonth()]} {d.getFullYear()}
                </option>
              );
            })}
          </select>
          {charge ? (
            <>
              <strong>{repondus}</strong> client{repondus > 1 ? 's' : ''} sur{' '}
              <strong>{lignes.length}</strong>{' '}
              {repondus > 1 ? 'ont envoyé leurs' : 'a envoyé ses'} variables ·{' '}
              <strong>{bulletinsFaits}</strong> bulletin
              {bulletinsFaits > 1 ? 's' : ''} fait
              {bulletinsFaits > 1 ? 's' : ''} · <strong>{dsnFaites}</strong> DSN
              envoyée{dsnFaites > 1 ? 's' : ''}
              {enSouffrance > 0 && (
                <span className="font-medium text-amber-700">
                  {' '}
                  · {enSouffrance} dossier{enSouffrance > 1 ? 's' : ''}{' '}
                  {enSouffrance > 1 ? 'attendent' : 'attend'} depuis plus de
                  deux jours
                </span>
              )}
            </>
          ) : (
            'Chargement…'
          )}
        </p>

        {peutAgir && relancables > 0 && (
          <button
            type="button"
            disabled={encours}
            onClick={() => void relancer()}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--color-primary-600)] px-3 py-1.5 text-[12.5px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
            {encours
              ? 'Envoi…'
              : `Relancer ${relancables} client${relancables > 1 ? 's' : ''}`}
          </button>
        )}
      </div>

      {bilan && (
        <p className="rounded-lg bg-[var(--color-primary-100)] px-3 py-2 text-[12.5px] text-[var(--color-primary-600)]">
          {bilan.envoyees} relance{bilan.envoyees > 1 ? 's' : ''} envoyée
          {bilan.envoyees > 1 ? 's' : ''}
          {bilan.ignorees > 0 &&
            `, ${bilan.ignorees} dossier${bilan.ignorees > 1 ? 's' : ''} laissé${bilan.ignorees > 1 ? 's' : ''} tranquille${bilan.ignorees > 1 ? 's' : ''}`}
          .
        </p>
      )}

      {/* Les titres de colonnes : sans eux, trois dates alignées ne disent pas
          de quoi elles parlent. */}
      <div className="hidden items-center gap-3 px-3 pb-1 text-[11px] tracking-wide text-neutral-400 uppercase md:flex">
        <span className="w-[320px] shrink-0">Client</span>
        <span className="w-[140px] shrink-0 pl-[20px]">Variables</span>
        <span className="w-[140px] shrink-0 pl-[20px]">Bulletins</span>
        <span className="w-[140px] shrink-0 pl-[20px]">DSN</span>
        <span className="flex-1" />
      </div>

      <div className="flex flex-col gap-3">
        {visibles.map(l => (
          <div
            key={l.folderUuid}
            className="group relative flex items-center gap-3 rounded-xl border border-[var(--color-primary-100)] bg-white px-3 py-2.5 transition-all hover:border-[var(--color-primary-400)] hover:shadow-sm"
          >
            {/* Toute la ligne mène au dossier : c'est là qu'on va comprendre
                pourquoi une case est rouge. Un calque de lien, pour garder le
                clic droit et l'ouverture dans un nouvel onglet. */}
            <Link
              to={`/folders/${l.folderUuid}`}
              className="absolute inset-0 rounded-xl focus:ring-2 focus:ring-[var(--color-primary-400)] focus:outline-none"
              aria-label={`Ouvrir le dossier ${l.dossier}`}
            />

            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-neutral-900 group-hover:text-[var(--color-primary-600)] md:w-[320px] md:flex-none">
              {l.dossier}
            </span>

            <span className="w-[140px] shrink-0">
              <CaseVariables ligne={l} />
            </span>
            <span className="hidden w-[140px] shrink-0 md:block">
              <Case etat={l.bulletins.etat} texte={l.bulletins.texte} />
            </span>
            <span className="hidden w-[140px] shrink-0 md:block">
              <Case etat={l.dsn.etat} texte={l.dsn.texte} />
            </span>

            <span className="flex-1" />

            {/*
              SAISIR À LA PLACE DU CLIENT — l'action se pose ICI, sur la ligne
              du dossier, à côté de « Reçu par mail ». C'est le même geste que
              celui qu'elle remplace : la gestionnaire est déjà là, devant la
              liste de ceux qui n'ont rien envoyé.
              Fiona TAVERNIER : « ça fait double boulot parce que ça l'a fait
              saisir, moi ça me refait saisir ».
            */}
            {/*
              ET SUR LES DOSSIERS DÉJÀ REÇUS AUSSI, où il dit « Voir sa
              saisie ». Ce lien était caché dès que le client avait répondu :
              la gestionnaire n'avait alors PLUS AUCUN chemin pour ouvrir
              l'écran de son client — ni pour relire ce qu'il avait envoyé, ni
              pour le lui montrer au téléphone. Constaté le 27/08/2026 : en
              démonstration, Rose n'a pas pu ouvrir cet écran sur le dossier
              qu'elle présentait, parce qu'il avait déjà répondu.

              Rien n'est écrasé en l'ouvrant : depuis le 25/08, l'écran reprend
              les lignes déjà envoyées et les renvoie avec (`lignesARenvoyer`),
              précisément pour qu'un second passage n'efface pas le premier.
            */}
            {(peutAgir || l.etat === 'recu') && (
              <Link
                to={`/collecte/${l.folderUuid}/saisie?mois=${mois}&annee=${annee}`}
                title={
                  l.etat === 'recu'
                    ? `Voir ce que ${l.dossier} a envoyé, sur son écran`
                    : `Saisir les variables de ${l.dossier} à la place du client`
                }
                /*
                  TOUJOURS VISIBLE, et ce n'est pas un oubli de discrétion.
                  Cette action était révélée au survol comme sa voisine. Or
                  c'est le SEUL chemin vers la saisie à la place du client :
                  cachée, la fonctionnalité n'existe pas pour qui ne la connaît
                  pas déjà. La distinction que Rose a posée le 25/08/2026 :
                  une information UTILE peut se replier, une information ou une
                  action NÉCESSAIRE se montre.
                  Elle reste discrète — c'est une action secondaire de ligne,
                  l'action principale de l'écran est « Relancer ».
                */
                className="relative z-10 flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[12px] text-[var(--color-primary-600)] transition-colors hover:bg-[var(--color-primary-100)]"
              >
                {l.etat === 'recu' ? (
                  <Eye className="h-3.5 w-3.5" />
                ) : (
                  <PencilLine className="h-3.5 w-3.5" />
                )}
                {l.etat === 'recu' ? 'Voir sa saisie' : 'Saisir'}
              </Link>
            )}

            {peutAgir && l.etat !== 'recu' && (
              <button
                type="button"
                disabled={encours}
                onClick={() => {
                  setDesignation(l);
                  setMotif('');
                }}
                title="Le client a envoyé ses variables autrement — par mail, par téléphone"
                className="relative z-10 flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[12px] text-neutral-500 transition-colors hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-600)]"
              >
                <Mail className="h-3.5 w-3.5" />
                Reçu par mail
              </button>
            )}

            {peutAgir && l.etat === 'recu_autrement' && (
              <button
                type="button"
                disabled={encours}
                onClick={async () => {
                  await collecteService.annulerRecuAutrement(
                    l.folderUuid,
                    mois,
                    annee
                  );
                  await recharger();
                }}
                aria-label={`Retirer la désignation de ${l.dossier}`}
                title="Retirer cette désignation"
                className="relative z-10 shrink-0 rounded-lg p-1 text-neutral-300 hover:text-red-700"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}

        {charge && visibles.length === 0 && (
          <p className="rounded-xl border border-dashed border-[var(--color-primary-100)] px-4 py-6 text-center text-[13.5px] text-neutral-600">
            {seulementAFaire
              ? 'Rien à faire : le mois est bouclé.'
              : 'Aucun dossier pour ce mois.'}
          </p>
        )}
      </div>

      {/* Le repli vers la liste complète, discret : il sert à vérifier, pas à
          travailler. */}
      {lignes.length > 0 && (
        <button
          type="button"
          onClick={() => setSeulementAFaire(v => !v)}
          className="text-[12px] text-neutral-400 underline-offset-2 hover:text-[var(--color-primary-600)] hover:underline"
        >
          {seulementAFaire
            ? `Montrer aussi les ${finis} dossiers terminés`
            : 'Ne montrer que ce qui demande un geste'}
        </button>
      )}

      <Modal
        isOpen={designation !== null}
        onClose={() => setDesignation(null)}
        title="Variables reçues autrement"
        size="sm"
      >
        <p className="mb-2 text-[13px] text-neutral-600">
          {designation?.dossier} ne sera plus relancé pour{' '}
          {MOIS[mois - 1].toLowerCase()} {annee}.
        </p>
        <label
          htmlFor="collecte-motif"
          className="mb-1 block text-[12.5px] text-neutral-600"
        >
          Par quel moyen ?
        </label>
        <input
          id="collecte-motif"
          value={motif}
          onChange={e => setMotif(e.target.value)}
          autoFocus
          placeholder="Mail du 3 septembre, tableau en pièce jointe"
          className="w-full rounded-lg border border-[var(--color-primary-100)] px-3 py-2.5 text-[14px] outline-none placeholder:text-neutral-400/50 focus:ring-2 focus:ring-[var(--color-primary-400)]"
        />
        <p className="mt-1.5 text-[11.5px] text-neutral-400">
          Écrit en clair : dans six mois, « reçu autrement » tout seul ne
          permettrait plus de retrouver ce qui est arrivé.
        </p>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={encours || !motif.trim()}
            onClick={() => void designer()}
            className="flex-1 rounded-xl bg-[var(--color-primary-600)] px-4 py-3 text-[14px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            Enregistrer
          </button>
          <button
            type="button"
            onClick={() => setDesignation(null)}
            className="rounded-xl border border-[var(--color-primary-100)] px-4 py-3 text-[14px] text-neutral-600 hover:border-[var(--color-primary-400)]"
          >
            Annuler
          </button>
        </div>
      </Modal>
    </div>
  );
}
